// app/contexts/ConsentContext.tsx
'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'; // useCallback hinzugefügt

// Cookie name for storing consent preferences
export const CONSENT_COOKIE_NAME = 'guideful-consent';
const COOKIE_EXPIRY_DAYS = 365;

// Service configuration
export interface ConsentService {
  id: string;
  name: string;
  description: string;
  category: 'technical' | 'marketing';
}

// The services we track consent for
const CONSENT_SERVICES: ConsentService[] = [
  { id: 'session', name: 'Session Management', description: '...', category: 'technical' },
  { id: 'csrf', name: 'CSRF Protection', description: '...', category: 'technical' },
  { id: 'consent', name: 'Cookie-Einstellungen', description: '...', category: 'technical' },
  { id: 'posthog', name: 'PostHog Analytics', description: '...', category: 'marketing' },
  { id: 'facebook', name: 'Meta Pixel', description: '...', category: 'marketing' },
  { id: 'googleAds', name: 'Google Ads', description: '...', category: 'marketing' }
];

// Default consent values
const DEFAULT_CONSENTS = {
  technical: true,
  marketing: false
};

// Type für den Consent-State
export type ConsentState = typeof DEFAULT_CONSENTS;

// Context state type
interface ConsentContextType {
  isInitialized: boolean;
  showBanner: boolean;
  consents: ConsentState;
  services: ConsentService[];
  updateConsent: (category: 'technical' | 'marketing', value: boolean) => void;
  acceptAll: () => void;
  acceptRequired: () => void;
  saveConsents: () => void; // Behalten, falls der Banner dies nutzt
  openConsentModal: () => void; // Behalten für Flexibilität
  closeConsentModal: () => void; // Behalten für Flexibilität
}

// Create context with default values
const ConsentContext = createContext<ConsentContextType>({
  isInitialized: false,
  showBanner: false,
  consents: DEFAULT_CONSENTS,
  services: CONSENT_SERVICES,
  updateConsent: () => {},
  acceptAll: () => {},
  acceptRequired: () => {},
  saveConsents: () => {},
  openConsentModal: () => {},
  closeConsentModal: () => {},
});

// Helper function to save consent to cookie
function saveConsentToCookie(consents: ConsentState) {
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + COOKIE_EXPIRY_DAYS);
  const cookieValue = encodeURIComponent(JSON.stringify(consents)); // Encoding hinzufügen

  document.cookie = `${CONSENT_COOKIE_NAME}=${cookieValue}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax; Secure`; // Secure Flag hinzufügen

  // Dispatch a custom event for other components to react to (optional, falls useConsentCheck darauf hört)
  const event = new CustomEvent('consent-updated', { detail: consents });
  window.dispatchEvent(event);
  console.log('[ConsentProvider] Consent saved to cookie and event dispatched:', consents);
}

interface ConsentProviderProps {
  children: ReactNode;
}

// Provider component
export function ConsentProvider({ children }: ConsentProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [consents, setConsents] = useState<ConsentState>(DEFAULT_CONSENTS);

  // Initialize consent state from cookie or defaults
  useEffect(() => {
    if (typeof window === 'undefined') return;

    console.log('[ConsentProvider] Initializing consent state...');
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${CONSENT_COOKIE_NAME}=`));

    let initialConsents = { ...DEFAULT_CONSENTS }; // Starte mit Defaults

    if (cookieValue) {
      try {
        const cookieString = cookieValue.split('=')[1];
        const savedConsents = JSON.parse(decodeURIComponent(cookieString));
        // Validiere und setze den gespeicherten Zustand, stelle sicher dass 'technical' true ist
        initialConsents = {
          technical: true, // Immer true
          marketing: savedConsents.marketing === true // Explizit prüfen
        };
        setShowBanner(false); // Kein Banner nötig, wenn Cookie gültig ist
        console.log('[ConsentProvider] Loaded consents from cookie:', initialConsents);
      } catch (error) {
        console.error('[ConsentProvider] Error parsing consent cookie, using defaults and showing banner.', error);
        setShowBanner(true); // Zeige Banner bei Fehler oder fehlendem Cookie
      }
    } else {
      console.log('[ConsentProvider] No consent cookie found, using defaults and showing banner.');
      setShowBanner(true);
    }

    setConsents(initialConsents);
    setIsInitialized(true);
    // Das gtag update passiert im nächsten useEffect, der auf diese State-Änderung reagiert
  }, []);

  // Update a specific consent category (wird vom Modal/Detailauswahl genutzt)
  const updateConsent = useCallback((category: 'technical' | 'marketing', value: boolean) => {
    if (category === 'technical' && !value) return; // Technical nicht deaktivierbar

    // Wichtig: Aktualisiere den State, aber speichere noch nicht direkt im Cookie.
    // Das Speichern erfolgt über acceptAll, acceptRequired oder saveConsents.
    setConsents(prev => {
      const newState = { ...prev, [category]: value };
      console.log('[ConsentProvider] Consent state changed (not saved yet):', newState);
      return newState;
    });
  }, []); // useCallback für stabile Funktion

  // Accept all consent options
  const acceptAll = useCallback(() => {
    const allConsents: ConsentState = { technical: true, marketing: true };
    setConsents(allConsents); // State aktualisieren
    saveConsentToCookie(allConsents); // Speichern & Event auslösen
    setShowBanner(false);
  }, []); // useCallback

  // Accept only required consents
  const acceptRequired = useCallback(() => {
    const requiredConsents: ConsentState = { technical: true, marketing: false };
    setConsents(requiredConsents); // State aktualisieren
    saveConsentToCookie(requiredConsents); // Speichern & Event auslösen
    setShowBanner(false);
  }, []); // useCallback

  // Save current consent selections (z.B. nach Detailauswahl im Modal)
  const saveConsents = useCallback(() => {
    // Stelle sicher, dass 'technical' immer true ist, bevor gespeichert wird
    const consentsToSave = { ...consents, technical: true };
    setConsents(consentsToSave); // Update state just in case
    saveConsentToCookie(consentsToSave); // Speichern & Event auslösen
    setShowBanner(false);
  }, [consents]); // Abhängigkeit von consents

  // Consent modal controls
  const openConsentModal = useCallback(() => setShowBanner(true), []);
  const closeConsentModal = useCallback(() => setShowBanner(false), []);


  // **************************************************************************
  // *** NEUER UseEffect: Sendet Consent Updates an Google via gtag         ***
  // **************************************************************************
  useEffect(() => {
    // Nur ausführen, wenn initialisiert und im Client-Kontext
    if (!isInitialized || typeof window === 'undefined') {
      console.log('[ConsentProvider-GtagUpdate] Skipping gtag update (not initialized or server-side).');
      return;
    }

    // Prüfen, ob gtag-Funktion verfügbar ist
    if (typeof window.gtag === 'function') {
      // Leite den Status für Google Consent Mode von unserem `consents`-State ab
      const adStorageState = consents.marketing ? 'granted' : 'denied';
      // Annahme: Analytics (falls relevant für Google) hängt ebenfalls von Marketing ab
      const analyticsStorageState = consents.marketing ? 'granted' : 'denied';
      // Optional weitere Parameter für Consent Mode v2 hinzufügen, falls benötigt
      // const adUserDataState = consents.marketing ? 'granted' : 'denied';
      // const adPersonalizationState = consents.marketing ? 'granted' : 'denied';

      console.log(`[ConsentProvider-GtagUpdate] Sending update to gtag: ad_storage=${adStorageState}, analytics_storage=${analyticsStorageState}`);

      // Sende das Update an gtag
      window.gtag('consent', 'update', {
        'ad_storage': adStorageState,
        'analytics_storage': analyticsStorageState,
        // 'ad_user_data': adUserDataState,
        // 'ad_personalization': adPersonalizationState,
      });
    } else {
      // Diese Warnung sollte nach der Initialisierung nicht mehr oft erscheinen,
      // da gtag (aus RootLayout) vorhanden sein sollte.
      console.warn('[ConsentProvider-GtagUpdate] window.gtag function not found when trying to update consent.');
    }

    // Dieser Effekt soll immer laufen, wenn sich der `consents`-State ändert oder
    // die Initialisierung abgeschlossen wird.
  }, [consents, isInitialized]);
  // **************************************************************************
  // *** Ende des neuen UseEffects                                          ***
  // **************************************************************************


  // Context value
  const contextValue: ConsentContextType = {
    isInitialized,
    showBanner,
    consents,
    services: CONSENT_SERVICES,
    updateConsent, // Wird vom Modal genutzt
    acceptAll,     // Wird vom Banner/Modal genutzt
    acceptRequired,// Wird vom Banner/Modal genutzt
    saveConsents,  // Wird vom Modal genutzt
    openConsentModal,
    closeConsentModal,
  };

  return (
    <ConsentContext.Provider value={contextValue}>
      {children}
    </ConsentContext.Provider>
  );
}

// Hook to use the consent context
export function useConsent() {
  const context = useContext(ConsentContext);
  if (context === undefined) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
}